import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { humanizeAxiosError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback (fragment session_id present),
    // skip the /me check — AuthCallback will exchange it first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (identifier, password, remember_me = false) => {
    try {
      const { data } = await api.post("/auth/login", { identifier, password, remember_me });
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: humanizeAxiosError(e) };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("logout call failed:", e?.message);
    }
    setUser(null);
  }, []);

  const register = useCallback(async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      return { ok: true, message: data.message };
    } catch (e) {
      return { ok: false, error: humanizeAxiosError(e) };
    }
  }, []);

  const googleSession = useCallback(async (session_id) => {
    try {
      const { data } = await api.post("/auth/google-session", { session_id });
      if (data.user) setUser(data.user);
      return { ok: true, ...data };
    } catch (e) {
      return { ok: false, error: humanizeAxiosError(e) };
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, register, googleSession, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
